// Category → color metadata for badges + accent lines on task cards.
// Use Record<string,...> so consumers can index with any string at runtime.

export interface PillarColor {
  bg: string;
  accent: string;
}

export const PILLAR_META: Record<string, PillarColor> = {
  "🌅 Morning Routine":   { bg: "#fff8ee", accent: "#e8a23a" },
  "🌙 Toddler Bedtime":   { bg: "#f0f0ff", accent: "#7b6fd4" },
  "🌙 Evening Wind-Down": { bg: "#f5f0ff", accent: "#9b6dd4" },
  "💪 Workout":           { bg: "#fff0fb", accent: "#d46db0" },
  "🌳 Outdoor Play":      { bg: "#f0fdf4", accent: "#4caf66" },
  "💇 Hair":              { bg: "#fdf6e8", accent: "#c8834a" },
  "🧺 Laundry":           { bg: "#f0f4ff", accent: "#5b7fe8" },
  "😴 Toddler Nap":       { bg: "#f0f8ff", accent: "#5b9fe8" },
  "🍽 Meals":             { bg: "#fff8f0", accent: "#e8834a" },
  "🛁 Bathroom":          { bg: "#fff0f0", accent: "#e87070" },
  "🛏 Bedroom":           { bg: "#f5f0fd", accent: "#a06bd4" },
  "🍳 Kitchen":           { bg: "#fff8f0", accent: "#e8834a" },
  "🛋 Living Room":       { bg: "#f0fbfd", accent: "#4ab8d4" },
  "🍽 Dining Room":       { bg: "#fdf5f0", accent: "#d48b6a" },
  "🖥 Office":            { bg: "#f0f5fd", accent: "#5b8fe8" },
  "🥗 Meal Prep":         { bg: "#f0fdf5", accent: "#4cb87a" },
  "🥗 Meal Planning":     { bg: "#f0fdf5", accent: "#4cb87a" },
  "🛒 Groceries":         { bg: "#fdfbf0", accent: "#c8b43a" },
  "💆 Self-Care":         { bg: "#fdf0f8", accent: "#d46ba0" },
  "📞 Social":            { bg: "#f0f8ff", accent: "#4a90d4" },
  "👶 Toddler Dev":       { bg: "#fffbf0", accent: "#e8b43a" },
  "💑 Us Time":           { bg: "#fff0f5", accent: "#e86a8a" },
  "💰 Admin: Finance":    { bg: "#f5fff0", accent: "#5ab85a" },
  "🏥 Admin: Health":     { bg: "#f0fff8", accent: "#3ab8a0" },
  "🔧 Admin: Home":       { bg: "#f8f5f0", accent: "#b87840" },
  "✅ Reset":             { bg: "#f8fdf0", accent: "#78b840" },
  "🔗 Connection":        { bg: "#fff0f5", accent: "#e86a8a" },
  "🏠 Home Mgmt":         { bg: "#f0f8ff", accent: "#4a7ab8" },
  "📅 Monthly Check":     { bg: "#f0fff8", accent: "#2ab8a0" },
  "🌱 Personal Growth":   { bg: "#f5fbf0", accent: "#5a9e5a" },
};

export interface PillarOption {
  key: string;
  label: string;
  icon: string;
}

export const PILLARS: PillarOption[] = [
  { key: "all",        label: "Timeline",  icon: "🕐" },
  { key: "morning",    label: "Morning",   icon: "🌅" },
  { key: "personal",   label: "Personal",  icon: "🌟" },
  { key: "connection", label: "Connect",   icon: "🔗" },
  { key: "toddler",    label: "Toddler",   icon: "👶" },
  { key: "cleaning",   label: "Cleaning",  icon: "🧹" },
  { key: "evening",    label: "Evening",   icon: "🌙" },
  { key: "homemgmt",   label: "Home Mgmt", icon: "🏠" },
];

export const PILLAR_CATS: Record<string, string[]> = {
  morning:    ["🌅 Morning Routine"],
  personal:   ["💪 Workout", "💆 Self-Care", "💇 Hair", "🌱 Personal Growth"],
  connection: ["🔗 Connection"],
  toddler:    ["👶 Toddler Dev", "🌳 Outdoor Play", "🌙 Toddler Bedtime", "😴 Toddler Nap"],
  cleaning:   [
    "🛁 Bathroom", "🛏 Bedroom", "🍳 Kitchen", "🛋 Living Room",
    "🍽 Dining Room", "🖥 Office", "🧺 Laundry", "✅ Reset",
  ],
  evening:    ["🌙 Evening Wind-Down"],
  homemgmt:   ["🏠 Home Mgmt"],
};

export interface PillarDescription {
  emoji: string;
  title: string;
  desc: string;
  color: string;
  accent: string;
}

export const PILLAR_DESCRIPTIONS: Record<string, PillarDescription> = {
  morning: {
    emoji: "🌅", title: "Morning Routine",
    desc: "Girl, how you start your morning really does set the whole vibe. Even if it's a little messy — you showed up.",
    color: "#fff8ee", accent: "#e8a23a",
  },
  personal: {
    emoji: "🌟", title: "Personal",
    desc: "You cannot pour from an empty cup, sis. This is your refill. Protect it like you mean it.",
    color: "#fff0fb", accent: "#d46db0",
  },
  connection: {
    emoji: "🔗", title: "Connection",
    desc: "Your people need you and you need them. Don't let the busy weeks make you drift — this is how you stay close.",
    color: "#fff0f5", accent: "#e86a8a",
  },
  toddler: {
    emoji: "👶", title: "Toddler",
    desc: "The snacks, the songs, the outside time, the cuddles — it all counts. They don't need perfect, they need you. And you're here.",
    color: "#fffbf0", accent: "#e8b43a",
  },
  cleaning: {
    emoji: "🧹", title: "Cleaning",
    desc: "One room. That's the deal. You're not a maid, you're a mom who keeps a real home — and it doesn't have to be perfect.",
    color: "#f0fbfd", accent: "#4ab8d4",
  },
  evening: {
    emoji: "🌙", title: "Evening",
    desc: "You made it through the day. Now feed your family, tuck in your baby, and actually let yourself rest. You earned it.",
    color: "#f5f0ff", accent: "#9b6dd4",
  },
  homemgmt: {
    emoji: "🏠", title: "Home Management",
    desc: "Not the glamorous stuff, but honestly? This is what keeps everything from falling apart. You're basically the CEO of this house.",
    color: "#f0f8ff", accent: "#4a7ab8",
  },
};

/** Returns the accent color for a task category, falling back to a soft tan. */
export function getCatColor(cat: string): string {
  return PILLAR_META[cat]?.accent ?? "#b07a50";
}
